import { Suspense, useEffect, VoidFunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { RouteContent } from 'components/app';
import { Notifications } from 'components/elements';
import { LoadingOverlay } from 'components/overlays';
import routes from 'config/routes';
import { getSystemConfig, getSystemInitialized, systemReady } from 'store/system';
import { useSP6KeepAlive } from 'hooks';

export const App: VoidFunctionComponent = () => {
    // Notify SP6 parent window of user activity so it can reset its session timeout timer
    useSP6KeepAlive();

    // Retrieve application store dispatcher
  const dispatch = useDispatch();

    // Retrieve application configuration
    const config = useSelector(getSystemConfig);

  // Detect if application has been initialized (configured) or the user is currently unauthorized
  const systemInitialized = useSelector(getSystemInitialized);

  useEffect(() => {
    dispatch(systemReady());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Display loading overlay while the application is initializing
  if (!config || !systemInitialized) {
    return <LoadingOverlay key="suspense" />;
  }

  return (
    <Suspense fallback={<LoadingOverlay key="suspense" />}>
      <Notifications />
      <BrowserRouter basename={config.app.baseUrl}>
        <BlockedRouteGuard />
        <Routes>
          {routes.map(({ id, path, ...rest }) => (
            <Route
              caseSensitive={true}
              element={<RouteContent id={id} {...rest} />}
              key={id}
              path={path}
            />
          ))}
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};

// Custom Guard component to block users if needed
const BlockedRouteGuard: VoidFunctionComponent = () => {
  const location = useLocation();
  const isBlocked = localStorage.getItem('bankrec_blocked') === 'true';

  if (isBlocked && location.pathname !== "/error") {
    return <Navigate to="/error" replace />;
  }

  return null; // render nothing otherwise
};
